class RegionalCommonUtils {
  // ===============================
  // УНИВЕРСАЛЬНЫЕ ВАЛИДАТОРЫ
  // ===============================
  
  /**
   * Валидирует LEI код (используется в разных странах)
   * @param {string} value - LEI для валидации
   * @returns {string|null} Ошибка валидации или null если корректный
   */
  static validateLEI(value) {
    if (typeof value !== 'string') return null;
    if (value.length !== 20) return 'LEI должен быть 20 символов длиной';
    // TODO: полная валидация LEI отключена из-за проблем с алгоритмом
    return null;
  }
  
  // ===============================
  // УНИВЕРСАЛЬНАЯ ПЛЮРАЛИЗАЦИЯ
  // ===============================
  
  /**
   * Определяет форму множественного числа для любой локали
   * @param {number} number - Число для определения формы
   * @param {string} locale - Код локали
   * @returns {string|null} Форма множественного числа или null
   */
  static getPluralFormByLocale(number, locale) {
    if (number == null || isNaN(number) || number < 0) {
      return null;
    }
    
    switch (locale.toUpperCase()) {
      case 'RU':
      case 'BY':
        // Русский/белорусский: сложная логика с тремя формами
        if (number % 1 !== 0) return 'many'; // дробные
        const lastDigit = number % 10;
        if (number > 10 && number < 15) return 'many'; // 11-14
        if (lastDigit === 1) return 'one'; // 1, 21, 31...
        if (lastDigit >= 2 && lastDigit <= 4) return 'few'; // 2-4, 22-24...
        return 'many'; // 0, 5-9, 10, 15-20...
      
      case 'EN':
      case 'DE':
      case 'ES':
      case 'GB':
      case 'CH':
      case 'IN':
      case 'PL':
        // Английский/немецкий/испанский и другие: простая логика
        return number === 1 ? 'one' : 'many';
      
      case 'UZ':
      case 'CN':
      case 'KZ':
      case 'TR':
        // Узбекский/китайский/казахский/турецкий: одна форма
        return 'one';
      
      default:
        return null;
    }
  }
  
  /**
   * Выбирает правильную форму слова в зависимости от числа и языка
   * Например: 1 день, 2 дня, 5 дней (русский) или 1 item, 2 items (английский)
   * @param {number} number - Число для которого выбираем форму слова
   * @param {string} oneForm - Форма для единственного числа (1 день, 1 item)
   * @param {string} fewForm - Форма для нескольких (2-4 дня) - только для языков типа русского
   * @param {string} manyForm - Форма для множественного числа (5+ дней, 2+ items)
   * @param {string} locale - Код языка ('RU', 'EN' и т.д.)
   * @returns {string|null} Подходящая форма слова или null если что-то неправильно
   *
   * @example
   * // Русский: oneForm="день", fewForm="дня", manyForm="дней"
   * pluralize(1, 'день', 'дня', 'дней', 'RU') // "день"
   * pluralize(3, 'день', 'дня', 'дней', 'RU') // "дня"
   * pluralize(10, 'день', 'дня', 'дней', 'RU') // "дней"
   *
   * // Английский: oneForm="item", manyForm="items" (fewForm игнорируется)
   * pluralize(1, 'item', null, 'items', 'EN') // "item"
   * pluralize(5, 'item', null, 'items', 'EN') // "items"
   */
  static pluralize(number, oneForm, fewForm, manyForm, locale) {
    if (typeof number !== 'number' || typeof oneForm !== 'string') {
      return null;
    }
    
    const pluralForm = RegionalCommonUtils.getPluralFormByLocale(number, locale);
    if (!pluralForm) return null;
    
    switch (locale.toUpperCase()) {
      case 'RU':
      case 'BY':
        // Нужны 3 формы: one, few, many
        if (typeof fewForm !== 'string' || typeof manyForm !== 'string') return null;
        switch (pluralForm) {
          case 'one': return oneForm;
          case 'few': return fewForm;
          case 'many': return manyForm;
          default: return null;
        }
      
      case 'EN':
      case 'DE':
      case 'ES':
      case 'GB':
      case 'CH':
      case 'IN':
      case 'PL':
        // Нужны 2 формы: singular, plural
        if (typeof manyForm !== 'string') return null;
        return pluralForm === 'one' ? oneForm : manyForm;
      
      case 'UZ':
      case 'CN':
      case 'KZ':
      case 'TR':
        // Нужна 1 форма (всегда одинаково)
        return oneForm;
      
      default:
        return null;
    }
  }
}

export default RegionalCommonUtils;